import React, { createContext, useContext, useState, useEffect } from 'react';

// Translations for Dutch, French, and English
const translations = {
  en: {
    // Navigation
    nav_home: 'Home',
    nav_search: 'Search',
    nav_map: 'Map',
    nav_saved: 'Saved',
    nav_profile: 'Profile',
    
    // Landing Page
    landing_badge: 'Family Adventures Await',
    landing_title_1: 'Discover',
    landing_title_2: 'Amazing',
    landing_title_3: 'Things To Do With Your Family',
    landing_description: 'Find the perfect activities for your children, from free local playgrounds to premium family experiences. Curated for parents, grandparents, and carers.',
    landing_cta_start: 'Get Started Free',
    landing_cta_explore: 'Explore Activities',
    landing_rating: 'Rating',
    landing_families: 'Families',
    landing_locations: 'Locations',
    
    // Features
    features_title: 'Everything You Need to Plan Family Fun',
    features_subtitle: 'From quick local discoveries to planning memorable holidays, InfoBel makes finding family activities effortless.',
    feature_smart_title: 'Smart Discovery',
    feature_smart_desc: 'AI-powered search understands natural queries like "rainy day activities for toddlers near me"',
    feature_location_title: 'Location-Based',
    feature_location_desc: 'Find activities near you with detailed maps, directions, and real-time availability',
    feature_save_title: 'Save & Share',
    feature_save_desc: 'Create personalized lists, save favorites, and share discoveries with family',
    
    // Categories
    categories_title: 'Explore by Category',
    categories_subtitle: 'From playgrounds to premium experiences',
    
    // CTA
    cta_title: 'Ready to Start Your Family Adventure?',
    cta_subtitle: 'Join thousands of families discovering amazing activities every day.',
    cta_button: 'Create Free Account',
    cta_google: 'Sign in with Google',
    
    // Footer
    footer_tagline: 'Making family time memorable.',
    
    // Auth
    auth_welcome_back: 'Welcome back',
    auth_sign_in_subtitle: 'Sign in to discover family activities',
    auth_create_account: 'Create your account',
    auth_create_subtitle: 'Start discovering family activities',
    auth_continue_google: 'Continue with Google',
    auth_or_email: 'or continue with email',
    auth_or_register: 'or register with email',
    auth_email: 'Email',
    auth_password: 'Password',
    auth_name: 'Name',
    auth_sign_in: 'Sign In',
    auth_create: 'Create Account',
    auth_forgot: 'Forgot password?',
    auth_no_account: "Don't have an account?",
    auth_have_account: 'Already have an account?',
    auth_create_one: 'Create one',
    auth_sign_in_link: 'Sign in',
    auth_role_label: 'I am a...',
    auth_role_parent: 'Parent / Carer',
    auth_role_parent_desc: 'Looking for activities for your children',
    auth_role_grandparent: 'Grandparent',
    auth_role_grandparent_desc: 'Planning outings with grandchildren',
    auth_role_venue: 'Venue / Business',
    auth_role_venue_desc: 'List and promote your activities',
    auth_password_min: 'At least 6 characters',
    
    // Discover
    discover_greeting: 'Hi,',
    discover_title: 'Discover',
    discover_search_placeholder: 'What are you looking for?',
    discover_load_more: 'Load More',
    discover_no_activities: 'No activities found',
    discover_clear_filters: 'Clear filters',
    
    // Tabs
    tab_for_you: 'For You',
    tab_near_me: 'Near Me',
    tab_trending: 'Trending',
    tab_budget: 'Budget',
    tab_premium: 'Premium',
    tab_weekend: 'Weekend',
    tab_holidays: 'Holidays',
    
    // Filters
    filter_title: 'Filters',
    filter_distance: 'Distance',
    filter_age: 'Age Suitability',
    filter_price: 'Price Range',
    filter_category: 'Category',
    filter_features: 'Features',
    filter_type: 'Type:',
    filter_indoor: 'Indoor',
    filter_outdoor: 'Outdoor',
    filter_both: 'Both',
    filter_grandparent: 'Grandparent Friendly',
    filter_buggy: 'Buggy Friendly',
    filter_reset: 'Reset',
    filter_apply: 'Apply Filters',
    
    // Age Groups
    age_0_2: 'Babies & Toddlers',
    age_3_5: 'Preschool',
    age_6_8: 'Early Primary',
    age_9_12: 'Tweens',
    age_13_plus: 'Teens',
    
    // Price
    price_free: 'Free',
    price_budget: 'Budget',
    price_mid: 'Mid-Range',
    price_premium: 'Premium',
    
    // Activity Card
    card_trending: 'Trending',
    card_grandparent: 'Grandparent Friendly',
    card_view_details: 'View Details',
    card_ages: 'Ages:',
    
    // Activity Detail
    detail_about: 'About',
    detail_why_love: 'Why families love it',
    detail_location: 'Location & Contact',
    detail_opening: 'Opening Times',
    detail_visit_website: 'Visit website',
    detail_view_map: 'View on Map',
    detail_reviews: 'Reviews',
    detail_write_review: 'Write Review',
    detail_no_reviews: 'No reviews yet. Be the first to review!',
    detail_rating: 'Rating',
    detail_comment: 'Share your experience...',
    detail_submit: 'Submit',
    detail_cancel: 'Cancel',
    detail_buggy: 'Buggy friendly',
    detail_parking: 'Parking available',
    detail_accessible: 'Accessible',
    detail_any_weather: 'Any weather',
    detail_visit: 'visit',
    
    // Search
    search_placeholder: 'Try "rainy day activities for toddlers"',
    search_ai_title: 'AI-Powered Search',
    search_ai_desc: 'Try natural queries like "things to do with a 4 year old near me today" or "cheap indoor activities this weekend"',
    search_popular: 'Popular Searches',
    search_recent: 'Recent Searches',
    search_trending_weekend: 'Trending this weekend',
    search_near_me: 'Near me',
    search_free: 'Free activities',
    search_results: 'results found',
    search_no_results: 'No results found',
    search_try_different: 'Try a different search term',
    search_searching: 'Searching with AI...',
    
    // Saved
    saved_title: 'Saved',
    saved_tab: 'Saved',
    saved_lists_tab: 'Lists',
    saved_no_activities: 'No saved activities yet',
    saved_tap_heart: 'Tap the heart icon on activities to save them for later',
    saved_discover: 'Discover Activities',
    saved_create_list: 'Create New List',
    saved_list_placeholder: 'e.g., Summer Ideas, Rainy Days',
    saved_create: 'Create',
    saved_organize: 'Create lists to organize your saved activities',
    saved_activities: 'activities',
    
    // Profile
    profile_edit: 'Edit Profile',
    profile_saved: 'Saved Activities',
    profile_lists: 'My Lists',
    profile_notifications: 'Notifications',
    profile_preferences: 'Preferences',
    profile_help: 'Help & Support',
    profile_logout: 'Sign Out',
    profile_dashboard: 'Dashboard',
    profile_venue_dashboard: 'Venue Dashboard',
    profile_admin_dashboard: 'Admin Dashboard',
    profile_account: 'Account',
    
    // Map
    map_title: 'Map View',
    map_list_view: 'List View',
    map_activities_nearby: 'Activities Nearby',
    map_view_all: 'View All',
    map_interactive: 'Interactive Map',
    
    // Onboarding
    onboarding_skip: 'Skip for now',
    onboarding_step: 'Step',
    onboarding_of: 'of',
    onboarding_continue: 'Continue',
    onboarding_back: 'Back',
    onboarding_start: 'Start Exploring',
    onboarding_q1_title: 'What ages are your children?',
    onboarding_q1_subtitle: 'Select all that apply',
    onboarding_q2_title: 'Budget & Travel',
    onboarding_q2_subtitle: 'Help us find activities that fit your lifestyle',
    onboarding_budget_pref: 'Budget preference',
    onboarding_travel: 'How far are you willing to travel?',
    onboarding_q3_title: 'What are you interested in?',
    onboarding_q3_subtitle: 'Select at least one',
    onboarding_q4_title: 'Final Preferences',
    onboarding_q4_subtitle: 'Almost done!',
    onboarding_days: 'Preferred days',
    onboarding_weekday: 'Weekdays',
    onboarding_weekend: 'Weekends',
    onboarding_holiday: 'School Holidays',
    onboarding_location: 'Indoor or outdoor?',
    
    // Interests
    interest_playgrounds: 'Playgrounds',
    interest_museums: 'Museums',
    interest_farms: 'Farms',
    interest_soft_play: 'Soft Play',
    interest_nature: 'Nature',
    interest_swimming: 'Swimming',
    interest_sports: 'Sports',
    interest_crafts: 'Crafts',
    interest_theatre: 'Theatre',
    interest_educational: 'Educational',
    
    // Admin
    admin_title: 'Admin Dashboard',
    admin_subtitle: 'Manage InfoBel platform',
    admin_overview: 'Overview',
    admin_pending: 'Pending',
    admin_users: 'Users',
    admin_total_users: 'Total Users',
    admin_activities: 'Activities',
    admin_reviews: 'Reviews',
    admin_quick_actions: 'Quick Actions',
    admin_review_pending: 'Review Pending',
    admin_manage_users: 'Manage Users',
    admin_view_feed: 'View Feed',
    admin_analytics: 'Analytics',
    admin_pending_activities: 'Pending Activities',
    admin_no_pending: 'No pending activities to review',
    admin_all_users: 'All Users',
    admin_user: 'User',
    admin_email: 'Email',
    admin_role: 'Role',
    admin_joined: 'Joined',
    
    // Venue Dashboard
    venue_title: 'Venue Dashboard',
    venue_subtitle: 'Manage your activities',
    venue_my_activities: 'My Activities',
    venue_add_activity: 'Add Activity',
    venue_analytics: 'Analytics',
    venue_total_views: 'Total Views',
    venue_total_saves: 'Total Saves',
    venue_total_reviews: 'Total Reviews',
    venue_avg_rating: 'Avg Rating',
    venue_no_activities: 'No activities yet',
    venue_add_first: 'Add your first activity to start reaching families!',
    venue_form_title: 'Activity Title',
    venue_form_summary: 'Short Summary',
    venue_form_why_love: 'Why Families Love It',
    venue_form_category: 'Category',
    venue_form_ages: 'Age Suitability',
    venue_form_price: 'Price Range',
    venue_form_type: 'Indoor/Outdoor',
    venue_form_address: 'Address',
    venue_form_city: 'City',
    venue_form_postcode: 'Postcode',
    venue_form_opening: 'Opening Times',
    venue_form_website: 'Website',
    venue_form_phone: 'Phone',
    venue_form_parking: 'Parking Info',
    venue_form_accessibility: 'Accessibility Info',
    venue_form_buggy: 'Buggy Friendly',
    venue_form_grandparent: 'Grandparent Friendly',
    venue_form_submit: 'Submit for Review',
    venue_form_update: 'Update Activity',
    venue_pending: 'Pending Approval',
    venue_approved: 'Approved',
    venue_rejected: 'Rejected',
    
    // Common
    common_loading: 'Loading...',
    common_error: 'Something went wrong',
    common_success: 'Success',
    common_save: 'Save',
    common_delete: 'Delete',
    common_edit: 'Edit',
    common_view: 'View',
    common_close: 'Close',
    common_add_photos: 'Add Photos',
    common_upload: 'Upload',
    common_view_all: 'View All',
    
    // Near Me
    near_me_not_supported: 'Geolocation is not supported by your browser',
    near_me_denied: 'Location access was denied. Please enable location in your browser settings to find activities near you.',
    near_me_locating: 'Finding your location...',
    near_me_allow: 'Please allow location access when prompted',
    near_me_error_title: 'Location Unavailable',
    near_me_retry: 'Try Again',
    near_me_showing: 'Showing',
    near_me_within: 'activities within',
    near_me_away: 'away',
    
    // Recommendations
    recommended_for_you: 'Recommended For You',
    
    // Collections
    collections_title_short: 'Collections',
    collections_page_title: 'Collections',
    collections_seasonal: 'Seasonal',
    collections_curated: 'Curated For You',
    
    // Search
    search_placeholder: 'Try "rainy day activities for toddlers"',
    search_ai_title: 'AI-Powered Search',
    search_ai_hint: 'Try natural queries like "things to do with a 4 year old near Antwerp" or "cheap indoor activities this weekend"',
    search_ai_thinking: 'Searching with AI...',
    search_ai_understanding: 'Understanding your request',
    search_ai_understood: 'AI understood your search as:',
    search_results_found: 'results found',
    search_no_results: 'No results found',
    search_try_different: 'Try a different search term',
    search_popular: 'Popular Searches',
    search_recent: 'Recent Searches',
    search_clear_all: 'Clear all',
    search_trending_weekend: 'Trending this weekend',
    search_near_me: 'Near me',
    search_free_activities: 'Free activities',
    
    // Saved page
    saved_title: 'Saved',
    saved_tab_saved: 'Saved',
    saved_tab_been_there: 'Been There',
    saved_tab_lists: 'Lists',
    saved_empty_title: 'No saved activities yet',
    saved_empty_desc: 'Tap the heart icon on activities to save them for later',
    saved_discover_btn: 'Discover Activities',
    saved_create_list: 'Create New List',
    saved_list_name_placeholder: 'e.g., Summer Ideas, Rainy Days',
    saved_list_desc_placeholder: 'Optional description...',
    saved_list_public: 'Make this list public',
    saved_lists_empty: 'Create lists to organize your saved activities',
    been_there_empty_title: 'No visits yet',
    been_there_empty_desc: 'Mark activities as "been there" to track your family adventures',
    common_cancel: 'Cancel',
    common_create: 'Create',
    
    // Comments
    comments_title: 'Comments',
    comments_empty: 'Be the first to comment!',
    comment_placeholder: 'Write a comment...',
    comment_reply: 'Reply',
    comment_write_reply: 'Write a reply...',
    comment_just_now: 'Just now',
    comment_login_required: 'Sign in to leave a comment',
    
    // Journal
    journal_title: 'Adventure Journal',
    journal_adventures: 'adventures',
    journal_empty_title: 'No adventures yet',
    journal_empty_desc: 'Mark activities as "been there" and add photos & notes to build your family adventure journal',
    journal_edit: 'Edit memories',
    journal_add_memories: 'Add photos & notes',
    journal_edit_title: 'Edit Adventure',
    journal_date: 'Visit Date',
    journal_note: 'Notes',
    journal_note_placeholder: 'What was your favourite part? Any tips for other families?',
    journal_photos: 'Photos',
    journal_add_photo: 'Add Photo',
    journal_save: 'Save',
    
    // Notifications
    notif_title: 'Notifications',
    notif_unread: 'unread',
    notif_mark_read: 'Mark all read',
    notif_empty_title: 'No notifications yet',
    notif_empty_desc: 'You\'ll see updates about comments, followers and new activities here',
    notif_replied: 'replied to your comment:',
    notif_followed: 'started following you',
    notif_new_nearby: 'New activity near you',
    notif_generic: 'You have a new notification',
  },
  
  nl: {
    // Navigation
    nav_home: 'Home',
    nav_search: 'Zoeken',
    nav_map: 'Kaart',
    nav_saved: 'Opgeslagen',
    nav_profile: 'Profiel',
    
    // Landing Page
    landing_badge: 'Familie Avonturen Wachten',
    landing_title_1: 'Ontdek',
    landing_title_2: 'Geweldige',
    landing_title_3: 'Dingen Om Te Doen Met Je Familie',
    landing_description: 'Vind de perfecte activiteiten voor je kinderen, van gratis speeltuinen tot premium familie-ervaringen. Samengesteld voor ouders, grootouders en verzorgers.',
    landing_cta_start: 'Gratis Beginnen',
    landing_cta_explore: 'Activiteiten Ontdekken',
    landing_rating: 'Beoordeling',
    landing_families: 'Families',
    landing_locations: 'Locaties',
    
    // Features
    features_title: 'Alles Wat Je Nodig Hebt Om Familie Plezier Te Plannen',
    features_subtitle: 'Van snelle lokale ontdekkingen tot het plannen van onvergetelijke vakanties, InfoBel maakt het vinden van familie activiteiten moeiteloos.',
    feature_smart_title: 'Slimme Ontdekking',
    feature_smart_desc: 'AI-gestuurde zoekfunctie begrijpt natuurlijke vragen zoals "regendag activiteiten voor peuters bij mij in de buurt"',
    feature_location_title: 'Locatie-Gebaseerd',
    feature_location_desc: 'Vind activiteiten bij jou in de buurt met gedetailleerde kaarten, routebeschrijvingen en real-time beschikbaarheid',
    feature_save_title: 'Opslaan & Delen',
    feature_save_desc: 'Maak gepersonaliseerde lijsten, sla favorieten op en deel ontdekkingen met familie',
    
    // Categories
    categories_title: 'Verken per Categorie',
    categories_subtitle: 'Van speeltuinen tot premium ervaringen',
    
    // CTA
    cta_title: 'Klaar Om Je Familie Avontuur Te Beginnen?',
    cta_subtitle: 'Sluit je aan bij duizenden families die elke dag geweldige activiteiten ontdekken.',
    cta_button: 'Gratis Account Maken',
    cta_google: 'Inloggen met Google',
    
    // Footer
    footer_tagline: 'Familie tijd onvergetelijk maken.',
    
    // Auth
    auth_welcome_back: 'Welkom terug',
    auth_sign_in_subtitle: 'Log in om familie activiteiten te ontdekken',
    auth_create_account: 'Maak je account aan',
    auth_create_subtitle: 'Begin met het ontdekken van familie activiteiten',
    auth_continue_google: 'Doorgaan met Google',
    auth_or_email: 'of doorgaan met email',
    auth_or_register: 'of registreren met email',
    auth_email: 'Email',
    auth_password: 'Wachtwoord',
    auth_name: 'Naam',
    auth_sign_in: 'Inloggen',
    auth_create: 'Account Maken',
    auth_forgot: 'Wachtwoord vergeten?',
    auth_no_account: 'Nog geen account?',
    auth_have_account: 'Heb je al een account?',
    auth_create_one: 'Maak er een',
    auth_sign_in_link: 'Inloggen',
    auth_role_label: 'Ik ben een...',
    auth_role_parent: 'Ouder / Verzorger',
    auth_role_parent_desc: 'Op zoek naar activiteiten voor je kinderen',
    auth_role_grandparent: 'Grootouder',
    auth_role_grandparent_desc: 'Uitjes plannen met kleinkinderen',
    auth_role_venue: 'Locatie / Bedrijf',
    auth_role_venue_desc: 'Vermeld en promoot je activiteiten',
    auth_password_min: 'Minimaal 6 tekens',
    
    // Discover
    discover_greeting: 'Hallo,',
    discover_title: 'Ontdek',
    discover_search_placeholder: 'Waar ben je naar op zoek?',
    discover_load_more: 'Meer Laden',
    discover_no_activities: 'Geen activiteiten gevonden',
    discover_clear_filters: 'Filters wissen',
    
    // Tabs
    tab_for_you: 'Voor Jou',
    tab_near_me: 'Dichtbij',
    tab_trending: 'Trending',
    tab_budget: 'Budget',
    tab_premium: 'Premium',
    tab_weekend: 'Weekend',
    tab_holidays: 'Vakanties',
    
    // Filters
    filter_title: 'Filters',
    filter_distance: 'Afstand',
    filter_age: 'Leeftijd Geschiktheid',
    filter_price: 'Prijsklasse',
    filter_category: 'Categorie',
    filter_features: 'Kenmerken',
    filter_type: 'Type:',
    filter_indoor: 'Binnen',
    filter_outdoor: 'Buiten',
    filter_both: 'Beide',
    filter_grandparent: 'Grootouder Vriendelijk',
    filter_buggy: 'Kinderwagen Vriendelijk',
    filter_reset: 'Reset',
    filter_apply: 'Filters Toepassen',
    
    // Age Groups
    age_0_2: "Baby's & Peuters",
    age_3_5: 'Kleuterschool',
    age_6_8: 'Lagere School',
    age_9_12: 'Tweens',
    age_13_plus: 'Tieners',
    
    // Price
    price_free: 'Gratis',
    price_budget: 'Budget',
    price_mid: 'Gemiddeld',
    price_premium: 'Premium',
    
    // Activity Card
    card_trending: 'Trending',
    card_grandparent: 'Grootouder Vriendelijk',
    card_view_details: 'Details Bekijken',
    card_ages: 'Leeftijden:',
    
    // Activity Detail
    detail_about: 'Over',
    detail_why_love: 'Waarom families het geweldig vinden',
    detail_location: 'Locatie & Contact',
    detail_opening: 'Openingstijden',
    detail_visit_website: 'Website bezoeken',
    detail_view_map: 'Op Kaart Bekijken',
    detail_reviews: 'Beoordelingen',
    detail_write_review: 'Schrijf Beoordeling',
    detail_no_reviews: 'Nog geen beoordelingen. Wees de eerste!',
    detail_rating: 'Beoordeling',
    detail_comment: 'Deel je ervaring...',
    detail_submit: 'Versturen',
    detail_cancel: 'Annuleren',
    detail_buggy: 'Kinderwagen vriendelijk',
    detail_parking: 'Parkeren beschikbaar',
    detail_accessible: 'Toegankelijk',
    detail_any_weather: 'Elk weer',
    detail_visit: 'bezoek',
    
    // Search
    search_placeholder: 'Probeer "regendag activiteiten voor peuters"',
    search_ai_title: 'AI-Gestuurde Zoekfunctie',
    search_ai_desc: 'Probeer natuurlijke vragen zoals "dingen om te doen met een 4-jarige bij mij vandaag" of "goedkope binnen activiteiten dit weekend"',
    search_popular: 'Populaire Zoekopdrachten',
    search_recent: 'Recente Zoekopdrachten',
    search_trending_weekend: 'Trending dit weekend',
    search_near_me: 'Bij mij in de buurt',
    search_free: 'Gratis activiteiten',
    search_results: 'resultaten gevonden',
    search_no_results: 'Geen resultaten gevonden',
    search_try_different: 'Probeer een andere zoekterm',
    search_searching: 'Zoeken met AI...',
    
    // Saved
    saved_title: 'Opgeslagen',
    saved_tab: 'Opgeslagen',
    saved_lists_tab: 'Lijsten',
    saved_no_activities: 'Nog geen opgeslagen activiteiten',
    saved_tap_heart: 'Tik op het hartje bij activiteiten om ze op te slaan',
    saved_discover: 'Activiteiten Ontdekken',
    saved_create_list: 'Nieuwe Lijst Maken',
    saved_list_placeholder: 'bijv. Zomer Ideeën, Regenachtige Dagen',
    saved_create: 'Maken',
    saved_organize: 'Maak lijsten om je opgeslagen activiteiten te organiseren',
    saved_activities: 'activiteiten',
    
    // Profile
    profile_edit: 'Profiel Bewerken',
    profile_saved: 'Opgeslagen Activiteiten',
    profile_lists: 'Mijn Lijsten',
    profile_notifications: 'Meldingen',
    profile_preferences: 'Voorkeuren',
    profile_help: 'Help & Ondersteuning',
    profile_logout: 'Uitloggen',
    profile_dashboard: 'Dashboard',
    profile_venue_dashboard: 'Locatie Dashboard',
    profile_admin_dashboard: 'Admin Dashboard',
    profile_account: 'Account',
    
    // Map
    map_title: 'Kaartweergave',
    map_list_view: 'Lijstweergave',
    map_activities_nearby: 'Activiteiten in de Buurt',
    map_view_all: 'Alles Bekijken',
    map_interactive: 'Interactieve Kaart',
    
    // Onboarding
    onboarding_skip: 'Overslaan voor nu',
    onboarding_step: 'Stap',
    onboarding_of: 'van',
    onboarding_continue: 'Doorgaan',
    onboarding_back: 'Terug',
    onboarding_start: 'Begin met Ontdekken',
    onboarding_q1_title: 'Welke leeftijden hebben je kinderen?',
    onboarding_q1_subtitle: 'Selecteer alle van toepassing',
    onboarding_q2_title: 'Budget & Reizen',
    onboarding_q2_subtitle: 'Help ons activiteiten te vinden die bij je levensstijl passen',
    onboarding_budget_pref: 'Budget voorkeur',
    onboarding_travel: 'Hoe ver wil je reizen?',
    onboarding_q3_title: 'Waar ben je in geïnteresseerd?',
    onboarding_q3_subtitle: 'Selecteer minstens één',
    onboarding_q4_title: 'Laatste Voorkeuren',
    onboarding_q4_subtitle: 'Bijna klaar!',
    onboarding_days: 'Voorkeursdagen',
    onboarding_weekday: 'Weekdagen',
    onboarding_weekend: 'Weekenden',
    onboarding_holiday: 'Schoolvakanties',
    onboarding_location: 'Binnen of buiten?',
    
    // Interests
    interest_playgrounds: 'Speeltuinen',
    interest_museums: 'Musea',
    interest_farms: 'Boerderijen',
    interest_soft_play: 'Soft Play',
    interest_nature: 'Natuur',
    interest_swimming: 'Zwemmen',
    interest_sports: 'Sport',
    interest_crafts: 'Knutselen',
    interest_theatre: 'Theater',
    interest_educational: 'Educatief',
    
    // Admin
    admin_title: 'Admin Dashboard',
    admin_subtitle: 'Beheer InfoBel platform',
    admin_overview: 'Overzicht',
    admin_pending: 'In Afwachting',
    admin_users: 'Gebruikers',
    admin_total_users: 'Totaal Gebruikers',
    admin_activities: 'Activiteiten',
    admin_reviews: 'Beoordelingen',
    admin_quick_actions: 'Snelle Acties',
    admin_review_pending: 'Bekijk In Afwachting',
    admin_manage_users: 'Beheer Gebruikers',
    admin_view_feed: 'Bekijk Feed',
    admin_analytics: 'Analyse',
    admin_pending_activities: 'Activiteiten in Afwachting',
    admin_no_pending: 'Geen activiteiten in afwachting om te beoordelen',
    admin_all_users: 'Alle Gebruikers',
    admin_user: 'Gebruiker',
    admin_email: 'Email',
    admin_role: 'Rol',
    admin_joined: 'Aangemeld',
    
    // Venue Dashboard
    venue_title: 'Locatie Dashboard',
    venue_subtitle: 'Beheer je activiteiten',
    venue_my_activities: 'Mijn Activiteiten',
    venue_add_activity: 'Activiteit Toevoegen',
    venue_analytics: 'Analyse',
    venue_total_views: 'Totaal Weergaven',
    venue_total_saves: 'Totaal Opgeslagen',
    venue_total_reviews: 'Totaal Beoordelingen',
    venue_avg_rating: 'Gem. Beoordeling',
    venue_no_activities: 'Nog geen activiteiten',
    venue_add_first: 'Voeg je eerste activiteit toe om families te bereiken!',
    venue_form_title: 'Activiteit Titel',
    venue_form_summary: 'Korte Samenvatting',
    venue_form_why_love: 'Waarom Families Het Geweldig Vinden',
    venue_form_category: 'Categorie',
    venue_form_ages: 'Leeftijd Geschiktheid',
    venue_form_price: 'Prijsklasse',
    venue_form_type: 'Binnen/Buiten',
    venue_form_address: 'Adres',
    venue_form_city: 'Stad',
    venue_form_postcode: 'Postcode',
    venue_form_opening: 'Openingstijden',
    venue_form_website: 'Website',
    venue_form_phone: 'Telefoon',
    venue_form_parking: 'Parkeer Info',
    venue_form_accessibility: 'Toegankelijkheid Info',
    venue_form_buggy: 'Kinderwagen Vriendelijk',
    venue_form_grandparent: 'Grootouder Vriendelijk',
    venue_form_submit: 'Indienen voor Beoordeling',
    venue_form_update: 'Activiteit Bijwerken',
    venue_pending: 'In Afwachting van Goedkeuring',
    venue_approved: 'Goedgekeurd',
    venue_rejected: 'Afgewezen',
    
    // Common
    common_loading: 'Laden...',
    common_error: 'Er ging iets mis',
    common_success: 'Succes',
    common_save: 'Opslaan',
    common_delete: 'Verwijderen',
    common_edit: 'Bewerken',
    common_view: 'Bekijken',
    common_close: 'Sluiten',
    common_add_photos: "Foto's Toevoegen",
    common_upload: 'Uploaden',
    common_view_all: 'Alles Bekijken',
    
    // Near Me
    near_me_not_supported: 'Geolocatie wordt niet ondersteund door je browser',
    near_me_denied: 'Locatietoegang is geweigerd. Schakel locatie in via je browserinstellingen om activiteiten bij jou in de buurt te vinden.',
    near_me_locating: 'Je locatie zoeken...',
    near_me_allow: 'Sta locatietoegang toe wanneer gevraagd',
    near_me_error_title: 'Locatie niet beschikbaar',
    near_me_retry: 'Probeer opnieuw',
    near_me_showing: 'Toont',
    near_me_within: 'activiteiten binnen',
    near_me_away: 'afstand',
    
    // Recommendations
    recommended_for_you: 'Aanbevolen voor jou',
    
    // Collections
    collections_title_short: 'Collecties',
    collections_page_title: 'Collecties',
    collections_seasonal: 'Seizoensgebonden',
    collections_curated: 'Speciaal voor jou',
    
    // Search
    search_placeholder: 'Probeer "regendag activiteiten voor peuters"',
    search_ai_title: 'AI-Zoeken',
    search_ai_hint: 'Probeer natuurlijke zoekopdrachten zoals "dingen om te doen met een 4-jarige in Antwerpen" of "goedkope binnenactiviteiten dit weekend"',
    search_ai_thinking: 'Zoeken met AI...',
    search_ai_understanding: 'Je verzoek begrijpen',
    search_ai_understood: 'AI heeft je zoekopdracht begrepen als:',
    search_results_found: 'resultaten gevonden',
    search_no_results: 'Geen resultaten gevonden',
    search_try_different: 'Probeer een andere zoekterm',
    search_popular: 'Populaire Zoekopdrachten',
    search_recent: 'Recente Zoekopdrachten',
    search_clear_all: 'Alles wissen',
    search_trending_weekend: 'Trending dit weekend',
    search_near_me: 'In de buurt',
    search_free_activities: 'Gratis activiteiten',
    
    // Saved page
    saved_title: 'Opgeslagen',
    saved_tab_saved: 'Opgeslagen',
    saved_tab_been_there: 'Geweest',
    saved_tab_lists: 'Lijsten',
    saved_empty_title: 'Nog geen opgeslagen activiteiten',
    saved_empty_desc: 'Tik op het hartje bij activiteiten om ze later te bewaren',
    saved_discover_btn: 'Ontdek Activiteiten',
    saved_create_list: 'Nieuwe Lijst Maken',
    saved_list_name_placeholder: 'bijv. Zomerideeën, Regendagen',
    saved_list_desc_placeholder: 'Optionele beschrijving...',
    saved_list_public: 'Maak deze lijst openbaar',
    saved_lists_empty: 'Maak lijsten om je opgeslagen activiteiten te ordenen',
    been_there_empty_title: 'Nog geen bezoeken',
    been_there_empty_desc: 'Markeer activiteiten als "geweest" om je gezinsavonturen bij te houden',
    common_cancel: 'Annuleren',
    common_create: 'Maken',
    
    // Comments
    comments_title: 'Reacties',
    comments_empty: 'Wees de eerste om te reageren!',
    comment_placeholder: 'Schrijf een reactie...',
    comment_reply: 'Reageer',
    comment_write_reply: 'Schrijf een reactie...',
    comment_just_now: 'Zojuist',
    comment_login_required: 'Log in om een reactie te plaatsen',
    
    // Journal
    journal_title: 'Avonturen Dagboek',
    journal_adventures: 'avonturen',
    journal_empty_title: 'Nog geen avonturen',
    journal_empty_desc: 'Markeer activiteiten als "geweest" en voeg foto\'s & notities toe aan je gezinsavonturen dagboek',
    journal_edit: 'Herinneringen bewerken',
    journal_add_memories: 'Foto\'s & notities toevoegen',
    journal_edit_title: 'Avontuur Bewerken',
    journal_date: 'Bezoekdatum',
    journal_note: 'Notities',
    journal_note_placeholder: 'Wat was je favoriete deel? Tips voor andere gezinnen?',
    journal_photos: 'Foto\'s',
    journal_add_photo: 'Foto Toevoegen',
    journal_save: 'Opslaan',
    
    // Notifications
    notif_title: 'Meldingen',
    notif_unread: 'ongelezen',
    notif_mark_read: 'Alles gelezen',
    notif_empty_title: 'Nog geen meldingen',
    notif_empty_desc: 'Je ziet hier updates over reacties, volgers en nieuwe activiteiten',
    notif_replied: 'reageerde op je reactie:',
    notif_followed: 'volgt je nu',
    notif_new_nearby: 'Nieuwe activiteit bij jou in de buurt',
    notif_generic: 'Je hebt een nieuwe melding',
  },
  
  fr: {
    // Navigation
    nav_home: 'Accueil',
    nav_search: 'Rechercher',
    nav_map: 'Carte',
    nav_saved: 'Favoris',
    nav_profile: 'Profil',
    
    // Landing Page
    landing_badge: 'Aventures Familiales à Découvrir',
    landing_title_1: 'Découvrez',
    landing_title_2: 'Incroyables',
    landing_title_3: 'Activités à Faire en Famille',
    landing_description: 'Trouvez les activités parfaites pour vos enfants, des aires de jeux gratuites aux expériences familiales premium. Sélectionné pour les parents, grands-parents et accompagnants.',
    landing_cta_start: 'Commencer Gratuitement',
    landing_cta_explore: 'Explorer les Activités',
    landing_rating: 'Note',
    landing_families: 'Familles',
    landing_locations: 'Lieux',
    
    // Features
    features_title: 'Tout Ce Dont Vous Avez Besoin Pour Planifier des Moments en Famille',
    features_subtitle: 'Des découvertes locales rapides à la planification de vacances mémorables, InfoBel facilite la recherche d\'activités familiales.',
    feature_smart_title: 'Recherche Intelligente',
    feature_smart_desc: 'La recherche IA comprend les requêtes naturelles comme "activités jour de pluie pour tout-petits près de moi"',
    feature_location_title: 'Basé sur la Localisation',
    feature_location_desc: 'Trouvez des activités près de chez vous avec des cartes détaillées, des itinéraires et la disponibilité en temps réel',
    feature_save_title: 'Sauvegarder & Partager',
    feature_save_desc: 'Créez des listes personnalisées, sauvegardez vos favoris et partagez vos découvertes avec votre famille',
    
    // Categories
    categories_title: 'Explorer par Catégorie',
    categories_subtitle: 'Des aires de jeux aux expériences premium',
    
    // CTA
    cta_title: 'Prêt à Commencer Votre Aventure Familiale?',
    cta_subtitle: 'Rejoignez des milliers de familles qui découvrent chaque jour des activités incroyables.',
    cta_button: 'Créer un Compte Gratuit',
    cta_google: 'Se connecter avec Google',
    
    // Footer
    footer_tagline: 'Rendre les moments en famille inoubliables.',
    
    // Auth
    auth_welcome_back: 'Bon retour',
    auth_sign_in_subtitle: 'Connectez-vous pour découvrir des activités familiales',
    auth_create_account: 'Créez votre compte',
    auth_create_subtitle: 'Commencez à découvrir des activités familiales',
    auth_continue_google: 'Continuer avec Google',
    auth_or_email: 'ou continuer avec email',
    auth_or_register: "ou s'inscrire avec email",
    auth_email: 'Email',
    auth_password: 'Mot de passe',
    auth_name: 'Nom',
    auth_sign_in: 'Se Connecter',
    auth_create: 'Créer un Compte',
    auth_forgot: 'Mot de passe oublié?',
    auth_no_account: "Pas encore de compte?",
    auth_have_account: 'Vous avez déjà un compte?',
    auth_create_one: 'Créer un',
    auth_sign_in_link: 'Se connecter',
    auth_role_label: 'Je suis...',
    auth_role_parent: 'Parent / Accompagnant',
    auth_role_parent_desc: 'À la recherche d\'activités pour vos enfants',
    auth_role_grandparent: 'Grand-parent',
    auth_role_grandparent_desc: 'Planifier des sorties avec les petits-enfants',
    auth_role_venue: 'Lieu / Entreprise',
    auth_role_venue_desc: 'Listez et promouvez vos activités',
    auth_password_min: 'Au moins 6 caractères',
    
    // Discover
    discover_greeting: 'Bonjour,',
    discover_title: 'Découvrir',
    discover_search_placeholder: 'Que recherchez-vous?',
    discover_load_more: 'Charger Plus',
    discover_no_activities: 'Aucune activité trouvée',
    discover_clear_filters: 'Effacer les filtres',
    
    // Tabs
    tab_for_you: 'Pour Vous',
    tab_near_me: 'Près de Moi',
    tab_trending: 'Tendances',
    tab_budget: 'Budget',
    tab_premium: 'Premium',
    tab_weekend: 'Week-end',
    tab_holidays: 'Vacances',
    
    // Filters
    filter_title: 'Filtres',
    filter_distance: 'Distance',
    filter_age: 'Âge Approprié',
    filter_price: 'Gamme de Prix',
    filter_category: 'Catégorie',
    filter_features: 'Caractéristiques',
    filter_type: 'Type:',
    filter_indoor: 'Intérieur',
    filter_outdoor: 'Extérieur',
    filter_both: 'Les Deux',
    filter_grandparent: 'Adapté aux Grands-parents',
    filter_buggy: 'Adapté aux Poussettes',
    filter_reset: 'Réinitialiser',
    filter_apply: 'Appliquer les Filtres',
    
    // Age Groups
    age_0_2: 'Bébés & Tout-petits',
    age_3_5: 'Maternelle',
    age_6_8: 'Primaire',
    age_9_12: 'Préados',
    age_13_plus: 'Adolescents',
    
    // Price
    price_free: 'Gratuit',
    price_budget: 'Budget',
    price_mid: 'Moyen',
    price_premium: 'Premium',
    
    // Activity Card
    card_trending: 'Tendance',
    card_grandparent: 'Adapté aux Grands-parents',
    card_view_details: 'Voir les Détails',
    card_ages: 'Âges:',
    
    // Activity Detail
    detail_about: 'À Propos',
    detail_why_love: 'Pourquoi les familles adorent',
    detail_location: 'Lieu & Contact',
    detail_opening: 'Horaires d\'Ouverture',
    detail_visit_website: 'Visiter le site web',
    detail_view_map: 'Voir sur la Carte',
    detail_reviews: 'Avis',
    detail_write_review: 'Écrire un Avis',
    detail_no_reviews: 'Pas encore d\'avis. Soyez le premier!',
    detail_rating: 'Note',
    detail_comment: 'Partagez votre expérience...',
    detail_submit: 'Envoyer',
    detail_cancel: 'Annuler',
    detail_buggy: 'Adapté aux poussettes',
    detail_parking: 'Parking disponible',
    detail_accessible: 'Accessible',
    detail_any_weather: 'Tout temps',
    detail_visit: 'visite',
    
    // Search
    search_placeholder: 'Essayez "activités jour de pluie pour tout-petits"',
    search_ai_title: 'Recherche Alimentée par l\'IA',
    search_ai_desc: 'Essayez des requêtes naturelles comme "choses à faire avec un enfant de 4 ans près de moi aujourd\'hui" ou "activités intérieures pas chères ce week-end"',
    search_popular: 'Recherches Populaires',
    search_recent: 'Recherches Récentes',
    search_trending_weekend: 'Tendances ce week-end',
    search_near_me: 'Près de moi',
    search_free: 'Activités gratuites',
    search_results: 'résultats trouvés',
    search_no_results: 'Aucun résultat trouvé',
    search_try_different: 'Essayez un autre terme de recherche',
    search_searching: 'Recherche avec l\'IA...',
    
    // Saved
    saved_title: 'Favoris',
    saved_tab: 'Favoris',
    saved_lists_tab: 'Listes',
    saved_no_activities: 'Pas encore de favoris',
    saved_tap_heart: 'Appuyez sur le cœur des activités pour les sauvegarder',
    saved_discover: 'Découvrir des Activités',
    saved_create_list: 'Créer une Nouvelle Liste',
    saved_list_placeholder: 'ex: Idées d\'Été, Jours de Pluie',
    saved_create: 'Créer',
    saved_organize: 'Créez des listes pour organiser vos activités sauvegardées',
    saved_activities: 'activités',
    
    // Profile
    profile_edit: 'Modifier le Profil',
    profile_saved: 'Activités Sauvegardées',
    profile_lists: 'Mes Listes',
    profile_notifications: 'Notifications',
    profile_preferences: 'Préférences',
    profile_help: 'Aide & Support',
    profile_logout: 'Se Déconnecter',
    profile_dashboard: 'Tableau de Bord',
    profile_venue_dashboard: 'Tableau de Bord Lieu',
    profile_admin_dashboard: 'Tableau de Bord Admin',
    profile_account: 'Compte',
    
    // Map
    map_title: 'Vue Carte',
    map_list_view: 'Vue Liste',
    map_activities_nearby: 'Activités à Proximité',
    map_view_all: 'Voir Tout',
    map_interactive: 'Carte Interactive',
    
    // Onboarding
    onboarding_skip: 'Passer pour l\'instant',
    onboarding_step: 'Étape',
    onboarding_of: 'sur',
    onboarding_continue: 'Continuer',
    onboarding_back: 'Retour',
    onboarding_start: 'Commencer l\'Exploration',
    onboarding_q1_title: 'Quel âge ont vos enfants?',
    onboarding_q1_subtitle: 'Sélectionnez tout ce qui s\'applique',
    onboarding_q2_title: 'Budget & Déplacements',
    onboarding_q2_subtitle: 'Aidez-nous à trouver des activités adaptées à votre style de vie',
    onboarding_budget_pref: 'Préférence de budget',
    onboarding_travel: 'Jusqu\'où êtes-vous prêt à voyager?',
    onboarding_q3_title: 'Qu\'est-ce qui vous intéresse?',
    onboarding_q3_subtitle: 'Sélectionnez au moins un',
    onboarding_q4_title: 'Dernières Préférences',
    onboarding_q4_subtitle: 'Presque terminé!',
    onboarding_days: 'Jours préférés',
    onboarding_weekday: 'Jours de semaine',
    onboarding_weekend: 'Week-ends',
    onboarding_holiday: 'Vacances Scolaires',
    onboarding_location: 'Intérieur ou extérieur?',
    
    // Interests
    interest_playgrounds: 'Aires de Jeux',
    interest_museums: 'Musées',
    interest_farms: 'Fermes',
    interest_soft_play: 'Soft Play',
    interest_nature: 'Nature',
    interest_swimming: 'Natation',
    interest_sports: 'Sports',
    interest_crafts: 'Activités Manuelles',
    interest_theatre: 'Théâtre',
    interest_educational: 'Éducatif',
    
    // Admin
    admin_title: 'Tableau de Bord Admin',
    admin_subtitle: 'Gérer la plateforme InfoBel',
    admin_overview: 'Vue d\'Ensemble',
    admin_pending: 'En Attente',
    admin_users: 'Utilisateurs',
    admin_total_users: 'Total Utilisateurs',
    admin_activities: 'Activités',
    admin_reviews: 'Avis',
    admin_quick_actions: 'Actions Rapides',
    admin_review_pending: 'Examiner en Attente',
    admin_manage_users: 'Gérer les Utilisateurs',
    admin_view_feed: 'Voir le Fil',
    admin_analytics: 'Analyses',
    admin_pending_activities: 'Activités en Attente',
    admin_no_pending: 'Aucune activité en attente à examiner',
    admin_all_users: 'Tous les Utilisateurs',
    admin_user: 'Utilisateur',
    admin_email: 'Email',
    admin_role: 'Rôle',
    admin_joined: 'Inscrit',
    
    // Venue Dashboard
    venue_title: 'Tableau de Bord Lieu',
    venue_subtitle: 'Gérer vos activités',
    venue_my_activities: 'Mes Activités',
    venue_add_activity: 'Ajouter une Activité',
    venue_analytics: 'Analyses',
    venue_total_views: 'Total Vues',
    venue_total_saves: 'Total Sauvegardes',
    venue_total_reviews: 'Total Avis',
    venue_avg_rating: 'Note Moyenne',
    venue_no_activities: 'Pas encore d\'activités',
    venue_add_first: 'Ajoutez votre première activité pour atteindre les familles!',
    venue_form_title: 'Titre de l\'Activité',
    venue_form_summary: 'Résumé Court',
    venue_form_why_love: 'Pourquoi les Familles Adorent',
    venue_form_category: 'Catégorie',
    venue_form_ages: 'Âge Approprié',
    venue_form_price: 'Gamme de Prix',
    venue_form_type: 'Intérieur/Extérieur',
    venue_form_address: 'Adresse',
    venue_form_city: 'Ville',
    venue_form_postcode: 'Code Postal',
    venue_form_opening: 'Horaires d\'Ouverture',
    venue_form_website: 'Site Web',
    venue_form_phone: 'Téléphone',
    venue_form_parking: 'Info Parking',
    venue_form_accessibility: 'Info Accessibilité',
    venue_form_buggy: 'Adapté aux Poussettes',
    venue_form_grandparent: 'Adapté aux Grands-parents',
    venue_form_submit: 'Soumettre pour Examen',
    venue_form_update: 'Mettre à Jour l\'Activité',
    venue_pending: 'En Attente d\'Approbation',
    venue_approved: 'Approuvé',
    venue_rejected: 'Rejeté',
    
    // Common
    common_loading: 'Chargement...',
    common_error: 'Quelque chose s\'est mal passé',
    common_success: 'Succès',
    common_save: 'Sauvegarder',
    common_delete: 'Supprimer',
    common_edit: 'Modifier',
    common_view: 'Voir',
    common_close: 'Fermer',
    common_add_photos: 'Ajouter des Photos',
    common_upload: 'Télécharger',
    common_view_all: 'Voir Tout',
    
    // Near Me
    near_me_not_supported: 'La géolocalisation n\'est pas supportée par votre navigateur',
    near_me_denied: 'L\'accès à la localisation a été refusé. Activez la localisation dans les paramètres de votre navigateur pour trouver des activités près de chez vous.',
    near_me_locating: 'Recherche de votre position...',
    near_me_allow: 'Veuillez autoriser l\'accès à la localisation lorsque demandé',
    near_me_error_title: 'Position indisponible',
    near_me_retry: 'Réessayer',
    near_me_showing: 'Affichage de',
    near_me_within: 'activités dans un rayon de',
    near_me_away: 'de distance',
    
    // Recommendations
    recommended_for_you: 'Recommandé pour vous',
    
    // Collections
    collections_title_short: 'Collections',
    collections_page_title: 'Collections',
    collections_seasonal: 'Saisonnières',
    collections_curated: 'Sélectionnées pour vous',
    
    // Search
    search_placeholder: 'Essayez "activités jour de pluie pour tout-petits"',
    search_ai_title: 'Recherche par IA',
    search_ai_hint: 'Essayez des requêtes naturelles comme "choses à faire avec un enfant de 4 ans près d\'Anvers" ou "activités intérieures pas chères ce weekend"',
    search_ai_thinking: 'Recherche avec IA...',
    search_ai_understanding: 'Compréhension de votre demande',
    search_ai_understood: 'L\'IA a compris votre recherche comme:',
    search_results_found: 'résultats trouvés',
    search_no_results: 'Aucun résultat trouvé',
    search_try_different: 'Essayez un autre terme de recherche',
    search_popular: 'Recherches Populaires',
    search_recent: 'Recherches Récentes',
    search_clear_all: 'Tout effacer',
    search_trending_weekend: 'Tendances ce weekend',
    search_near_me: 'Près de moi',
    search_free_activities: 'Activités gratuites',
    
    // Saved page
    saved_title: 'Favoris',
    saved_tab_saved: 'Favoris',
    saved_tab_been_there: 'Visité',
    saved_tab_lists: 'Listes',
    saved_empty_title: 'Pas encore de favoris',
    saved_empty_desc: 'Appuyez sur le coeur des activités pour les sauvegarder',
    saved_discover_btn: 'Découvrir des Activités',
    saved_create_list: 'Créer une Nouvelle Liste',
    saved_list_name_placeholder: 'ex. Idées d\'été, Jours de pluie',
    saved_list_desc_placeholder: 'Description optionnelle...',
    saved_list_public: 'Rendre cette liste publique',
    saved_lists_empty: 'Créez des listes pour organiser vos activités sauvegardées',
    been_there_empty_title: 'Pas encore de visites',
    been_there_empty_desc: 'Marquez les activités comme "visité" pour suivre vos aventures familiales',
    common_cancel: 'Annuler',
    common_create: 'Créer',
    
    // Comments
    comments_title: 'Commentaires',
    comments_empty: 'Soyez le premier à commenter !',
    comment_placeholder: 'Écrire un commentaire...',
    comment_reply: 'Répondre',
    comment_write_reply: 'Écrire une réponse...',
    comment_just_now: 'À l\'instant',
    comment_login_required: 'Connectez-vous pour laisser un commentaire',
    
    // Journal
    journal_title: 'Journal d\'Aventures',
    journal_adventures: 'aventures',
    journal_empty_title: 'Pas encore d\'aventures',
    journal_empty_desc: 'Marquez les activités comme "visité" et ajoutez des photos et notes pour créer votre journal d\'aventures familiales',
    journal_edit: 'Modifier les souvenirs',
    journal_add_memories: 'Ajouter photos et notes',
    journal_edit_title: 'Modifier l\'Aventure',
    journal_date: 'Date de visite',
    journal_note: 'Notes',
    journal_note_placeholder: 'Quelle était votre partie préférée ? Des conseils pour d\'autres familles ?',
    journal_photos: 'Photos',
    journal_add_photo: 'Ajouter une Photo',
    journal_save: 'Enregistrer',
    
    // Notifications
    notif_title: 'Notifications',
    notif_unread: 'non lues',
    notif_mark_read: 'Tout marquer lu',
    notif_empty_title: 'Pas encore de notifications',
    notif_empty_desc: 'Vous verrez ici les mises à jour sur les commentaires, abonnés et nouvelles activités',
    notif_replied: 'a répondu à votre commentaire :',
    notif_followed: 'vous suit maintenant',
    notif_new_nearby: 'Nouvelle activité près de chez vous',
    notif_generic: 'Vous avez une nouvelle notification',
  }
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('infobel_language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('infobel_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setLanguage(lang);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    languages: [
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'nl', name: 'Nederlands', flag: '🇧🇪' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
